import { motion } from "framer-motion";
import { Zap, Factory, Orbit, ArrowUpRight, Youtube, ExternalLink, Cpu } from "lucide-react";

const YOUTUBE_CHANNEL = "https://www.youtube.com/@ranedynamics";

const fea = [
    {
        img: "https://customer-assets.emergentagent.com/job_rane-dynamics-films/artifacts/gv130sxf_IMG-20220412-WA0030.jpg",
        study: "Static Displacement",
        metric: "0.136 mm",
        metricLabel: "URES · max deflection",
        desc: "Torque load applied to the V1 chassis mount. Peak deflection under design load stays fractional — sub-millimetre.",
    },
    {
        img: "https://customer-assets.emergentagent.com/job_rane-dynamics-films/artifacts/klfvdmcc_IMG-20220412-WA0029.jpg",
        study: "von Mises Stress",
        metric: "5.86 × 10⁶",
        metricLabel: "N/m² · vs 2.76 × 10⁷ yield",
        desc: "Nodal stress under the same torque study. Peak sits at ~21% of the material's yield strength — comfortable margin.",
    },
    {
        img: "https://customer-assets.emergentagent.com/job_rane-dynamics-films/artifacts/zju6a5lo_IMG-20220412-WA0028.jpg",
        study: "Factor of Safety",
        metric: "FOS 4.71",
        metricLabel: "minimum, automatic criterion",
        desc: "Factor-of-safety distribution across the chassis. Design minimum 4.71× — nearly 5× over the worst-case load.",
    },
];

const videos = [
    {
        id: "VP6s7_7amOI",
        title: "From the Workshop",
        desc: "Latest build log from the Rane Dynamics YouTube channel.",
    },
    {
        id: "If8fwFwwkKA",
        title: "Field Notes",
        desc: "More from the shop — Rane Dynamics · @ranedynamics.",
    },
];

const pillars = [
    {
        id: "v1",
        num: "01",
        title: "V1 Electric Platform",
        tag: "Advanced Mobility",
        desc: "A production-ready electric two-wheeler engineered from the ground up as an open reference platform. Chevron-frame geometry, modular battery, service-first architecture. Two working prototypes operational.",
        img: "/rane/v1-bike.png",
        icon: Zap,
        span: "md:col-span-7",
    },
    {
        id: "sovereign",
        num: "02",
        title: "Sovereign Node",
        tag: "The Compiler · Micro-Factory",
        desc: "Self-contained, EI-orchestrated manufacturing pod. RDF-2 Octo-Nexus with 8 synchronous print heads, 12–16 kg/hr throughput, 160 sq ft footprint. Solar + battery sovereignty. Double capacity in 48 hours.",
        img: "/rane/mini-forge.png",
        icon: Factory,
        span: "md:col-span-5",
    },
    {
        id: "spectra",
        num: "03",
        title: "Spectra // Blackline",
        tag: "Narrative Engine",
        desc: "The universe that ties every prototype to a story. IP for books, films, games and future collectibles — a shared world where the engineering leads.",
        img: "https://customer-assets.emergentagent.com/job_rane-dynamics-films/artifacts/rwxcmxrs_Nano%20Banana%202%20-%20Professional%20YouTube%20channel%20banner%20for%20Rane%20Dynamics_%20cinematic%20sci-fi%20engineering_1%20%281%29.png",
        icon: Orbit,
        span: "md:col-span-12",
    },
];

export default function Engineering() {
    return (
        <section
            id="engineering"
            data-testid="engineering-section"
            className="relative bg-[#F4F4F5] py-24 md:py-40"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 md:mb-24">
                    <div className="md:col-span-7">
                        <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF5500]">
                            004 // Engineering
                        </span>
                        <h2 className="mt-4 font-display font-black text-5xl md:text-7xl leading-[0.9] tracking-tight text-zinc-950">
                            The Compiler.<br />
                            <span className="italic font-light">A distributed cloud</span><br />
                            for the physical world.
                        </h2>
                    </div>
                    <div className="md:col-span-5 md:pt-6">
                        <p className="text-lg text-zinc-600 leading-relaxed">
                            The trilogy imagines what&apos;s next. Our engineering builds it —
                            EI-orchestrated Sovereign Nodes that compile digital files into
                            physical assets in hours, not months.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
                    {pillars.map((p, i) => (
                        <motion.article
                            key={p.id}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.7, delay: i * 0.1 }}
                            data-testid={`engineering-pillar-${p.id}`}
                            className={`${p.span} group relative rounded-2xl overflow-hidden bg-white border border-black/5 hover:border-[#FF5500]/50 transition-colors duration-500`}
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 h-full">
                                <div className="p-8 md:p-10 flex flex-col justify-between order-2 md:order-1">
                                    <div>
                                        <div className="flex items-center justify-between mb-8">
                                            <span className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-400">
                                                {p.num} · {p.tag}
                                            </span>
                                            <p.icon size={20} className="text-[#FF5500]" />
                                        </div>
                                        <h3 className="font-display font-black text-3xl md:text-4xl leading-[0.95] tracking-tight text-zinc-950">
                                            {p.title}
                                        </h3>
                                        <p className="mt-6 text-zinc-600 leading-relaxed">
                                            {p.desc}
                                        </p>
                                    </div>
                                    <div className="mt-8 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-zinc-950 group-hover:text-[#FF5500] transition-colors">
                                        Learn more
                                        <ArrowUpRight
                                            size={16}
                                            className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                                        />
                                    </div>
                                </div>
                                <div className="relative order-1 md:order-2 min-h-[220px] md:min-h-[380px] overflow-hidden">
                                    <img
                                        src={p.img}
                                        alt={p.title}
                                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-tr from-black/30 via-transparent to-transparent" />
                                </div>
                            </div>
                        </motion.article>
                    ))}
                </div>

                {/* Structural Analysis / FEA strip */}
                <div className="mt-24 md:mt-32">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
                        <div>
                            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF5500]">
                                Structural Analysis · V1 Chassis
                            </span>
                            <h3 className="mt-3 font-display font-black text-3xl md:text-5xl leading-[0.95] tracking-tight text-zinc-950">
                                Real engineering,<br />
                                <span className="italic font-light">under load.</span>
                            </h3>
                        </div>
                        <div className="max-w-md text-sm md:text-base text-zinc-600 leading-relaxed">
                            SolidWorks FEA of the V1 chassis under a design torque study —
                            displacement, von Mises stress, and factor-of-safety plots straight
                            from the workshop.
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                        {fea.map((f, i) => (
                            <motion.figure
                                key={f.study}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.7, delay: i * 0.08 }}
                                data-testid={`fea-plot-${i + 1}`}
                                className="group rounded-2xl overflow-hidden bg-white border border-black/5 hover:border-[#FF5500]/50 transition-colors duration-500"
                            >
                                <div className="relative aspect-[4/3] bg-white overflow-hidden border-b border-black/5">
                                    <img
                                        src={f.img}
                                        alt={`V1 chassis ${f.study} — ${f.metric}`}
                                        className="absolute inset-0 w-full h-full object-contain transition-transform duration-[1400ms] ease-out group-hover:scale-105"
                                    />
                                    <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-white/90 backdrop-blur border border-black/5 px-2.5 py-1 rounded-full">
                                        <Cpu size={11} className="text-[#FF5500]" />
                                        <span className="text-[9px] font-bold uppercase tracking-[0.25em] text-zinc-700">
                                            FEA · {String(i + 1).padStart(2, "0")}
                                        </span>
                                    </div>
                                </div>
                                <figcaption className="p-6 md:p-7">
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2">
                                        {f.study}
                                    </div>
                                    <div className="font-display font-black text-3xl leading-none text-zinc-950">
                                        {f.metric}
                                    </div>
                                    <div className="mt-1.5 text-xs uppercase tracking-wider text-zinc-500">
                                        {f.metricLabel}
                                    </div>
                                    <p className="mt-4 text-sm text-zinc-600 leading-relaxed">
                                        {f.desc}
                                    </p>
                                </figcaption>
                            </motion.figure>
                        ))}
                    </div>

                    <p className="mt-8 text-xs uppercase tracking-[0.25em] text-zinc-500">
                        Study · Torque (default) · Chassis · SolidWorks Simulation
                    </p>
                </div>

                {/* Sovereign Mesh Economics */}
                <div className="mt-24 md:mt-32">
                    {/* RDF-2 Octo-Nexus hero banner */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.8 }}
                        data-testid="rdf2-hero"
                        className="relative rounded-3xl overflow-hidden border border-black/10 bg-black aspect-[21/9] mb-8"
                    >
                        <img
                            src="/rane/rdf2-octonexus.png"
                            alt="RDF-2 Octo-Nexus — 8-head synchronous gantry compiling a vehicle chassis under Blackwell EI orchestration"
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/20 to-transparent" />
                        <div className="absolute inset-0 p-8 md:p-14 flex flex-col justify-between">
                            <div className="inline-flex self-start items-center gap-2 bg-white/10 backdrop-blur-xl border border-white/20 px-3 py-1.5 rounded-full">
                                <Cpu size={12} className="text-[#FF5500]" />
                                <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-white">
                                    RDF-2 · Octo-Nexus
                                </span>
                            </div>
                            <div className="max-w-2xl">
                                <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF5500] mb-3">
                                    Node 1 · The Compiler
                                </div>
                                <h4 className="font-display font-black text-3xl md:text-5xl leading-[0.95] tracking-tight text-white">
                                    8 heads. One Script.<br />
                                    <span className="italic font-light">Zero retooling.</span>
                                </h4>
                                <p className="mt-4 text-sm md:text-base text-zinc-300 max-w-lg">
                                    NVIDIA Blackwell EI runs real-time physics simulation across 8
                                    synchronous print heads. 10,000 generations simulated before
                                    the first atom moves.
                                </p>
                            </div>
                        </div>
                    </motion.div>

                    <div className="relative rounded-3xl overflow-hidden bg-zinc-950 text-white p-8 md:p-14">
                    <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#FF5500] opacity-15 blur-[160px] pointer-events-none" />
                    <div className="relative z-10 grid grid-cols-1 md:grid-cols-12 gap-10">
                        <div className="md:col-span-5">
                            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF5500]">
                                Sovereign Mesh · Economics
                            </span>
                            <h3 className="mt-4 font-display font-black text-3xl md:text-5xl leading-[0.95] tracking-tight">
                                What a compiled<br />
                                factory looks like.
                            </h3>
                            <p className="mt-6 text-zinc-300 leading-relaxed max-w-md">
                                The RDF-2 Octo-Nexus is 8 print heads under one Script. The
                                Sovereign Mesh is 6 of them, distributed. These are the numbers
                                that come out.
                            </p>
                            <a
                                href="https://customer-assets.emergentagent.com/job_rane-dynamics-films/artifacts/3lxaez6r_RANE_DYNAMICS_PITCH_DECK_v3.pdf"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="download-deck-cta"
                                className="mt-8 inline-flex items-center gap-2 bg-[#FF5500] hover:bg-white hover:text-zinc-950 text-white font-semibold text-sm uppercase tracking-wider px-6 py-3.5 rounded-full transition-colors duration-300"
                            >
                                Download the Deck
                                <ExternalLink size={14} />
                            </a>
                        </div>

                        <div className="md:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-px bg-white/10 rounded-2xl overflow-hidden">
                            {[
                                { v: "8", l: "Print heads · one Script", s: "RDF-2 Octo-Nexus" },
                                { v: "12–16", l: "kg / hour throughput", s: "Material-agnostic" },
                                { v: "160", l: "sq ft footprint", s: "Per Compiler" },
                                { v: "$222K", l: "Profit / sq ft", s: "At two-shift" },
                                { v: "48 hrs", l: "Double capacity", s: "Mini-Forge scale-up" },
                                { v: "0.5 yr", l: "Solar payback", s: "75 kW · 225 kWh" },
                            ].map((m, i) => (
                                <div
                                    key={i}
                                    data-testid={`mesh-stat-${i + 1}`}
                                    className="bg-zinc-950 p-6 md:p-7 flex flex-col justify-between hover:bg-[#FF5500] transition-colors duration-500 group"
                                >
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 group-hover:text-white/80">
                                        {m.s}
                                    </div>
                                    <div>
                                        <div className="mt-4 font-display font-black text-3xl md:text-4xl leading-none">
                                            {m.v}
                                        </div>
                                        <div className="mt-2 text-xs uppercase tracking-wider text-zinc-400 group-hover:text-white">
                                            {m.l}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    </div>
                </div>

                {/* YouTube strip */}
                <div className="mt-24 md:mt-32">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
                        <div>
                            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF5500]">
                                From the Workshop
                            </span>
                            <h3 className="mt-3 font-display font-black text-3xl md:text-5xl leading-[0.95] tracking-tight text-zinc-950">
                                The build, on video.
                            </h3>
                        </div>
                        <a
                            href={YOUTUBE_CHANNEL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="youtube-channel-cta"
                            className="inline-flex items-center gap-2 bg-zinc-950 hover:bg-[#FF0000] text-white font-semibold text-sm uppercase tracking-wider px-6 py-3.5 rounded-full transition-colors duration-300 self-start md:self-auto"
                        >
                            <Youtube size={18} />
                            Subscribe on YouTube
                            <ExternalLink size={14} />
                        </a>
                    </div>

                    <div className={`grid grid-cols-1 ${videos.length > 1 ? "md:grid-cols-2" : ""} gap-6 md:gap-8`}>
                        {videos.map((v, i) => (
                            <motion.div
                                key={v.id + i}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.7, delay: i * 0.1 }}
                                data-testid={`youtube-embed-${i + 1}`}
                                className="group rounded-2xl overflow-hidden bg-white border border-black/5 hover:border-[#FF5500]/50 transition-colors duration-500"
                            >
                                <div className="relative aspect-video bg-black">
                                    <iframe
                                        src={`https://www.youtube.com/embed/${v.id}?rel=0`}
                                        title={v.title}
                                        loading="lazy"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                        allowFullScreen
                                        className="absolute inset-0 w-full h-full"
                                    />
                                </div>
                                <div className="p-6 md:p-8 flex items-start justify-between gap-6">
                                    <div>
                                        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-400 mb-2">
                                            YouTube · @ranedynamics
                                        </div>
                                        <div className="font-display font-bold text-xl md:text-2xl text-zinc-950">
                                            {v.title}
                                        </div>
                                        <p className="mt-2 text-sm text-zinc-600 max-w-md">{v.desc}</p>
                                    </div>
                                    <a
                                        href={`https://youtu.be/${v.id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`youtube-open-${i + 1}`}
                                        className="shrink-0 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-zinc-950 group-hover:text-[#FF5500] transition-colors"
                                    >
                                        Open
                                        <ArrowUpRight
                                            size={16}
                                            className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                                        />
                                    </a>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
